import cx from 'classnames';
import { useEffect, useMemo, useState } from 'react';
import { CommonGrid } from '../layouts';
import 'react-phone-input-2/lib/style.css';
import {
  TopButtonArea,
  SharePopup,
  ActionFilters,
  GlobalFilter,
  BigButton,
  StyledSelect,
} from '../common';
import _ from 'lodash';
import { SHARED_OPTIONS_URL_PARAMS, useScreenSettings } from '../helpers/configureHelper';
import { useSharedFiles } from '../hooks/useSharedFiles';
import InfiniteScroll from '../components/InfiniteScroll/InfiniteScroll';
import { ThumbnailContainerV2 } from '../common/ThumbnailContainerV2';
import { PropDetailsPopup } from '../common/PropDetailsPopup/PropDetailsPopup';
import { My3dModelDetailsPopupController } from '../common/My3dModelDetailsPopup/My3dModelDetailsPopup';
import AddToCollectionPopup from '../common/AddToCollectionPopup';
import BinIcon from '../components/icons/BinIcon';
import { ACTION } from '../common/ThumbnailCardUI';
import { usePageTitle } from '../hooks/usePageTitle';
import { EditViewTextureDetailsModal } from './MyProductsTextures';
import { PageSearchBar } from '../common/UniversalSearchForm/UniversalSearchForm';
import { PropsEditDetailsPopupController } from '../common/PropDetailsPopup/EditPropDetailsPopup';
import { EditRoomPopup } from '../common/EditRoomPopup/EditRoomPopup';
import { Outlet, useSearchParams } from 'react-router-dom';
import { getParamsFromFilterBy } from '../helpers/urlHelper';
import { addUpdateParamsInSequence } from '../redux/slicers/filters/filterSlicer';
import { useDispatch, useSelector } from 'react-redux';
import TopContainerV2 from '../layouts/TopContainerV2';
import { GlobalDefaultTeamMemberFilter, GlobalSearchTagsFilter } from '../common/GlobalFilter';
import FooterOptions from '../layouts/FooterOptions';
import FilterSideModalButton from '../common/FilterSideModalButton';
import { selectTeamMembersFilterOpen } from '../redux/slicers/uiSlicer';
import { totalItemCount } from '../helpers/jsHelper';
import { PlusIcon } from '../assets/icons';
import { navigate } from '../helpers/projectHelper';
import { INVITE_INTERNAL_USER } from '../constants/routePathConstants';
import { useTextureActions } from '../hooks/useTextureActions';
import { useDownloadEntity } from '../hooks/useDownloadEntity';
import DownloadIcon from '../components/icons/DownloadIcon';
import { MODEL_TYPES } from '../constants/constants';
import { DataListContextProvider } from '../contexts/DataListContext';

export default function SharedFiles({
  cardType = 'sharedFiles',
  useGlobalSearch,
  noDataText = 'Your shared files live here!',
  cardProps,
  children,
  designV2 = true,
  hideStickyHeader = null,
}) {
  const dispatch = useDispatch();
  const [searchParams, setSearchParams] = useSearchParams();
  const teamFilterModal = useSelector(selectTeamMembersFilterOpen);
  const [showDefaultFilter, setShowDefaultFilter] = useState(false);
  const title = 'Shared Files';
  usePageTitle(title);

  const pageSettings = useScreenSettings(cardType);
  const { filterOptions, sortOptions, defaultSortOption, showSharedFilter } = pageSettings;

  const {
    list,
    loading,
    fetchMoreData,
    hasMore,
    total,
    hasData,
    filterBy,
    sortBy,

    selected,
    anySelected,
    allSelected,
    selectedList,
    onFilterByChange,
    onSortChange,
    toggleAll,
    toggleItem,
    refetchList,
    search,
    onSearchChange,
  } = useSharedFiles({
    filterByKey: 'type',
    // requestParams,
    defaultSort: defaultSortOption,
    useGlobalSearch,
  });

  // updating template & prop card
  const handleCardUpdate = (data) => {
    refetchList();
  };

  const handleFilterByChange = (value) => {
    dispatch(addUpdateParamsInSequence({ filterBy: getParamsFromFilterBy(value, 'type') }));
    onFilterByChange(value);
  };

  const getNonSiloList = (l = []) =>
    l?.filter((a) => !(a?.data?.is_product_builder && a?.data?.type === '9')); // Temporary hide: Hide share option for product builder renders with Silo template (type '9')

  const filterListRecord = selected
    ? getNonSiloList(
        list?.filter(({ id, data }) => selected.includes(id) || selected.includes(data?.id))
      )
    : getNonSiloList(selectedList);

  const NoDataFound = () => {
    return (
      <div className="w-100 m-auto d-flex flex-column justify-content-center align-items-center">
        <h2>{noDataText}</h2>
        <p>All the files shared with you or your organization will be</p>
        <p>visible here. Add your team now & start collaborating.</p>
        <BigButton
          className="black-btn order-lg-5 lf-btn-1 keep-text"
          buttonIcon={PlusIcon}
          onClick={() => navigate(INVITE_INTERNAL_USER)}
          buttonTitle="Invite members"
        />
      </div>
    );
  };

  const filterByOptionsList = () => {
    const filterProps = {
      value: filterBy,
      data: filterOptions,
      onChange: handleFilterByChange,
      containerClass: 'top-filter',
    };
    const sortProps = {
      disabled: Boolean(search),
      value: sortBy,
      data: sortOptions,
      onChange: onSortChange,
      containerClass: 'top-filter',
    };

    const filterByValue = filterProps?.data?.find(
      (item) => `${item.value}` === `${filterProps?.value?.value}`
    );
    const sortByValue = sortProps?.data?.find(
      (item) => `${item.value}` === `${sortProps?.value?.value}`
    );

    const styledSelectInput = (value) => {
      return (
        <StyledSelect
          value={value}
          onChange={filterProps.onChange}
          options={filterProps.data}
          placeholder={filterProps?.placeholder || 'Filter by'}
          disabled={loading}
        />
      );
    };
    return (
      <div className={cx('filter_container')}>
        <div className="fc-left d-flex">
          {filterProps && filterProps.data && filterProps.data.length > 0 && (
            <div className={cx('minWidth165', filterProps.containerClass)}>
              {!filterByValue && styledSelectInput(null)}
              {filterByValue && styledSelectInput(filterByValue)}
            </div>
          )}
          {sortProps && sortProps.data && sortProps.data.length > 0 && (
            <div className={cx('minWidth165', sortProps.containerClass)}>
              <StyledSelect
                value={sortByValue}
                onChange={sortProps.onChange}
                options={sortProps.data}
                placeholder={'Sort by'}
                disabled={loading || sortProps?.disabled}
              />
            </div>
          )}
        </div>
      </div>
    );
  };
  const hasTextureModel = filterListRecord?.some((item) => item?.model_type === MODEL_TYPES.TEXTURE);
  const selectedRenders = filterListRecord?.filter(({ model_type }) =>
    [MODEL_TYPES.PROJECTRENDER, MODEL_TYPES.PREVIEWS].includes(model_type)
  );

  const { loading: isLoading, handleDownloadClick } = useTextureActions({
    list,
    from: 'sharedFiles',
    // The note only holds while textures really are all that comes down.
    infoMessage: selectedRenders?.length
      ? undefined
      : 'Only textures from your selection will be downloaded',
  });

  const { setAngleImageUrl } = useDownloadEntity();
  const [downloadingRenders, setDownloadingRenders] = useState(false);

  /**
   * Selected images download the same way as on the images page - every pass of
   * every angle, Post Edit included - alongside the textures the texture action
   * already handles.
   */
  const handleFooterDownload = async () => {
    if (hasTextureModel) handleDownloadClick(filterListRecord);
    if (!selectedRenders?.length) return;

    setDownloadingRenders(true);
    try {
      await setAngleImageUrl({
        selectedList: selectedRenders.map(({ data }) => data),
        is_separate: false,
      });
    } finally {
      setDownloadingRenders(false);
    }
  };

  return (
    <CommonGrid
      sticky={hideStickyHeader ? false : true}
      noDataText={search ? `You don't have any files shared with "${search}" name.` : NoDataFound()}
      className={(anySelected || allSelected) && designV2 ? 'show-action-footer' : ''}
      topContainer={
        <TopContainerV2
          subClassName="shareTab"
          globalFilter={
            !useGlobalSearch && (
              <div>
                <GlobalFilter designV2 />
                <GlobalSearchTagsFilter designV2={designV2} />
                <GlobalDefaultTeamMemberFilter designV2 showMe />
              </div>
            )
          }>
          <>
            {!useGlobalSearch && (
              <>
                <div className="totalCount">{totalItemCount(total)}</div>
                <PageSearchBar
                  designV2
                  model={''}
                  onSearchChange={onSearchChange}
                  search={search}
                  hideTags={true}
                />
                <FilterSideModalButton showTeamMemberFilter={true} showTeamFilter={true} />
                {filterByOptionsList()}
                {/* <ActionFilters
                  hasData={hasData}
                  loading={loading}
                  filterProps={{
                    value: filterBy,
                    data: filterOptions,
                    onChange: handleFilterByChange,
                    containerClass: 'top-filter',
                  }}
                  sortProps={{
                    value: sortBy,
                    data: sortOptions,
                    onChange: onSortChange,
                    containerClass: 'top-filter',
                  }}
                  showSharedFilter={false}
                  teamFilterModal={teamFilterModal}
                /> */}
              </>
            )}
          </>
        </TopContainerV2>
      }
      hasData={list.length > 0 || loading}
      item={cardType}
      list={list}>
      <InfiniteScroll
        dataLength={list.length}
        next={fetchMoreData}
        hasMore={hasMore}
        loading={loading}
        hasChildren={hasData}>
        <ThumbnailContainerV2
          designV2={designV2}
          isSharedPage
          isSharedItem={true}
          items={list}
          selected={selected}
          cardProps={{
            refetchList: refetchList,
            fullWidth: true,
            hideFavourite: true,
            onSelect: (id, data, originalData) => {
              toggleItem(id || data?.id || originalData.id);
            },
            customActions: {
              [ACTION.DELETE]: {
                label: 'Remove Access',
                onClick: null, // (id, data, originalData) => console.log("originalData: ", originalData), // ([originalData]),
                icon: <BinIcon />,
              },
            },
            ...cardProps,
          }}
          // TODO!!! deleteSharedModel: apiDeleteSharedDetails,
        />
        {hasMore && <div id="whiteShadow" />}
      </InfiniteScroll>

      {(anySelected || allSelected) && designV2 && (
        <FooterOptions>
          <>
            <div className="selected-item">
              <ActionFilters
                selectedProps={
                  list?.length > 0 && {
                    allSelected: allSelected,
                    onSelectAll: () => toggleAll(),
                  }
                }
              />
              <span>{selected?.length || 0} Selected</span>
            </div>
            <div className="footer-action">
              <TopButtonArea
                isSharedPage
                hasData={hasData}
                item={cardType}
                addNewButtonArea={null}
                loading={loading}
                // selectedList={filterListRecord}
                topButtonList={[
                  {
                    element: SharePopup,
                    bigButton: true,
                    bigBtnClass: 'lf-btn-2',
                    onSuccess: refetchList,
                    //model type 0 for mix files
                    modelType: 'sharedFiles',
                    selectedList: filterListRecord,
                  },
                  ...(hasTextureModel || selectedRenders?.length
                    ? [
                        {
                          element: BigButton,
                          bigBtnClass: 'lf-btn-1',
                          buttonTitle: 'Download',
                          iconBefore: <DownloadIcon />,
                          onClick: handleFooterDownload,
                          isLoading: isLoading || downloadingRenders,
                        },
                      ]
                    : []),
                ]}
                // anySelected={anySelected}

                anySelected={anySelected}
                selectedList={selectedList}
              />
            </div>
          </>
        </FooterOptions>
      )}

      <EditViewTextureDetailsModal />
      <PropDetailsPopup redirect />
      <EditRoomPopup onUpdateSuccess={handleCardUpdate} />
      <PropsEditDetailsPopupController onUpdateSuccess={handleCardUpdate} />
      <My3dModelDetailsPopupController />
      <AddToCollectionPopup />
      <ActionFilters
        hasData={hasData}
        loading={loading}
        showSharedFilter={false}
        teamFilterModal={teamFilterModal}
      />
      <DataListContextProvider
        fetchMoreData={fetchMoreData}
        hasMore={hasMore}
        list={list}
        refetchList={refetchList}>
        {!useGlobalSearch && <Outlet />}
        {children}
      </DataListContextProvider>
    </CommonGrid>
  );
}
